<template>

	<Teleport to="#popups">
		<div :id="id ? id : ''" class="overlay-wrapper" :class="{hidden: !isActive, noOverlay: currNoOverlay}" @click.self="clickOverlay" :style="zIndex ? 'z-index: '+zIndex : ''">
			<div class="bottom-sheet-container">
				<div class="bottom-sheet" ref="bottom-sheet" :style="getBottomSheetStyles()">
					<div class="bottom-sheet-measurer" ref="bottom-sheet-measurer" :class="{activeScroll: activeScrollbar}">
						<div class="bottom-sheet-content" ref="bottom-sheet-content">
							<slot></slot>
						</div>
					</div>
				</div>
			</div>
		</div>
	</Teleport>

</template>

<script>
import {useValuesStore} from "@/shared/stores";
import {lockBodyScroll, unlockBodyScroll} from "@/shared/helpers/scrollLock";

export default {
	setup() {
		const valuesStore = useValuesStore();
		return {valuesStore};
	},
	name: "AppBottomSheet",
	emits: ["toggling-popup", "click-overlay"],
	data() {
		return {
			isActive: this.active,
			activeScrollbar: false,
			currNoOverlay: this.noOverlay,
			escListener: null
		}
	},
	props: {
		id: {
			type: String,
			default: null
		},
		noOverlay: {
			type: Boolean,
			default: false
		},
		active: {
			type: Boolean,
			default: false
		},
		forceWidth: {
			type: String,
			default: null,
			validator(value) {
				return value === null || value.includes('px') || value.includes('%');
			}
		},
		forceBackgroundColor: {
			type: String,
			default: null,
		},
		undismissable: {
			type: Boolean,
			default: false
		},
		zIndex: {
			type: Number,
			default: null
		}
	},
	methods: {
		clickOverlay() {
			this.$emit('click-overlay');
			if(!this.undismissable) {
				this.toggleStatus(false);
			}
		},
		toggleStatus(newStatus = !this.isActive) {
			this.isActive = newStatus;
			this.updateScroll();
			this.$emit("toggling-popup", this.isActive);
		},
        updateScroll() {
            if(!this.currNoOverlay && this.isActive) {
                if(!this._scrollLocked) {
                    lockBodyScroll();
                    this._scrollLocked = true;
                }
            } else if(this._scrollLocked) {
                unlockBodyScroll();
                this._scrollLocked = false;
            }
        },
		getBottomSheetStyles() {
			let styles = {};
			if (this.forceWidth) {
				styles.width = this.forceWidth;
			}
			if (this.forceBackgroundColor) {
				styles.backgroundColor = this.forceBackgroundColor;
			}
			return styles;
		}
	},
	created() {
		if(!document.getElementById("popups")) {
			let popups = document.createElement("div");
			popups.id = "popups";
			document.body.appendChild(popups);
		}
	},
	mounted() {
		setTimeout(() => {
			if(this.isActive) {
				this.activeScrollbar = true;
			}
		}, 360);

		this.escListener = (e) => {
			if (e.keyCode === 27) {
				this.clickOverlay();
			}
		}
		document.addEventListener('keyup', this.escListener);

		if(this.isActive) {
			this.updateScroll();
		}
	},
	beforeUnmount() {
		document.removeEventListener('keyup', this.escListener);
		if(this._scrollLocked) {
			unlockBodyScroll();
			this._scrollLocked = false;
		}
	},
	watch: {
		active(newStatus) {
			this.toggleStatus(newStatus);
		},
		isActive(newStatus) {
			if(newStatus) {
				setTimeout(() => {
					if(this.isActive) {
						this.activeScrollbar = true;
					}
				}, 360);
			} else {
				this.activeScrollbar = false;
			}
		},
		forceWidth() {
			this.$refs["bottom-sheet"].style = this.getBottomSheetStyles();
		},
		forceBackgroundColor() {
			this.$refs["bottom-sheet"].style = this.getBottomSheetStyles();
		},
		noOverlay() {
			this.currNoOverlay = this.noOverlay;
            this.updateScroll();
		},
		"valuesStore.forceCloseAllBottomSheets"(newStatus) {
			if(newStatus) {
				this.toggleStatus(false);
			}
		}
	}
}
</script>

<style lang="scss" scoped>
.overlay-wrapper {
	z-index: 1000;
	@include centered;
	position: fixed;
	bottom: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: var(--color-bg-overlay);
	backdrop-filter: blur(5px);
	-webkit-backdrop-filter: blur(5px);
	visibility: visible;
	transition: visibility 0.3s $transition, background-color .3s $transition, opacity .3s $transition;

	.bottom-sheet-container {
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		align-items: center;
		width: 100%;
		height: 100%;
		transition: opacity .2s $transition .1s, transform .2s $transition .1s;
		opacity: 1;
		transform: translateY(0);
		pointer-events: none;

		.bottom-sheet {
			width: 100%;
			background-color: var(--color-bg-primary-1);
			max-height: calc(95% - 30px);
			padding: 20px;
			border-radius: 20px 20px 0 0;
			pointer-events: all;
			box-shadow: $box_shadow_inset;
			transition: height 0.3s ease;
			position: relative;

			.bottom-sheet-measurer {
				overflow-y: hidden;
				overflow-x: hidden;
				@include hideScrollbar();
				height: 100%;

				.bottom-sheet-content {
					overflow-y: auto;
					height: auto;

				}

				&.activeScroll {
					overflow-y: auto;
				}

			}

		}
	}

	&.hidden {
		visibility: hidden;
		background-color: transparent;
		opacity: 0;
		pointer-events: none;

		.bottom-sheet-container {
			opacity: 0;
			transform: translateY(calc(100vh + 200px));
			transition: opacity .2s $transition, transform .2s $transition;
		}
	}

	&.noOverlay {
		background-color: transparent;
		pointer-events: none;
		backdrop-filter: none;
		-webkit-backdrop-filter: none;

		.bottom-sheet-container .bottom-sheet {
			box-shadow: 0 -5px 80px var(--color-box-shadow-3);
		}
	}

}
</style>